// React
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// FutureLab
import { RootState } from '../../reducers';
import { SendChatMessage } from '../../components/chat/SendChatMessage';
import { useSocket } from '../../hooks/useSocket';
import { ChatParticipantsInterface } from './ChatListItemsMobile';
import { ChatRoomMessage } from './chatRoom/ChatRoomMessage';
import { ChatRoomMessageDetails } from './chatRoom/ChatRoomMessageDetails';
import { ScrollToBottom } from './chatRoom/ScrollToBottom';
import { ChatRoomAttachmentFile } from './chatRoom/ChatRoomAttachmentFile';
import { updateChatList } from '../../utilities/Chat';
import { isSameUserId, toUserId } from '../../utilities/UserId';
import { useLazyGetChatMessagesQuery, useMarkMessagesSeenMutation } from '../../store/api/chatApi';
import type { QueuedAttachment } from './attachment';

// Redux
import { store } from '../../ReduxRoot';

// 3rd Party
import _ from 'lodash';
import moment from 'moment';

export interface ChatRoomItemsInterface {
	pinned: PinnedMessageInterface[];
	attachments: any[];
	chatId: string;
	createdAt: string;
	id: number;
	message: string;
	participantId: number;
	participant?: number;
	replyTo: object;
	seenBy: number[];
}

export interface DeleteMessagePayloadInterface {
	chatId: string;
	messageId: string;
}

export interface PinnedMessageInterface {
	chatId: string;
	chatMessageId: ChatRoomItemsInterface;
	id: number;
	participantId: number;
}

export function ChatRoom() {
	// useState initializers
	const [scrollToBottomNewMessage, setScrollToBottomNewMessage] = useState(false);
	const [lastSeenMessagedIds, setLastSeenMessagedIds] = useState<number[]>([]);
	const [lastSeenMessagedIdsSent, setLastSeenMessagedIdsSent] = useState<number[]>([]);
	const [chatRoomItems, setChatRoomItems] = useState<ChatRoomItemsInterface[]>([]);
	const [newSeenMessages, setNewSeenMessages] = useState<ChatRoomItemsInterface[]>([]);
	const [newMessage, setNewMessage] = useState<any>({});
	const [loggedInUserParticipantId, setLoggedInUserParticipantId] = useState<ChatParticipantsInterface>();
	const [hasMoreMessages, setHasMoreMessages] = useState(true);
	const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
	const [files, setFiles] = useState<QueuedAttachment[]>([]);

	// useSelectors initializers
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const loggedInUserDetails = useSelector((state: RootState) => state.loggedInUserDetails);
	const pinnedMessageInfocus = useSelector((state: RootState) => state.pinnedMessageInfocus);
	const chatListsItems = useSelector((state: RootState) => state.chatList);

	// Socket setup
	const socket = useSocket();
	const [loadChatMessages] = useLazyGetChatMessagesQuery();
	const [markMessagesSeen] = useMarkMessagesSeenMutation();
	// html reference for binding the screen to the scrolling being triggered by the user
	const messagesListRef = useRef<HTMLDivElement>(null);
	const chatIdRef = useRef<number | string | undefined>();
	const nextMessageOffsetRef = useRef(0);
	const isLoadingOlderMessagesRef = useRef(false);
	const pendingInitialScrollRef = useRef(false);

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const scrollMessagesToLatest = (behavior: ScrollBehavior = 'auto') => {
		const container = messagesListRef.current;
		if (!container) return;

		if (behavior === 'smooth') {
			container.scrollTo({ top: container.scrollHeight, behavior });
		} else {
			container.scrollTop = container.scrollHeight;
		}
	};

	useEffect(() => {
		if (pinnedMessageInfocus) {
			scrollToPInnedMessage();
		}
	}, [pinnedMessageInfocus]);

	const scrollToPInnedMessage = () => {
		const targetMessage = document.getElementsByClassName(`${pinnedMessageInfocus.chatMessageId.id}`);
		targetMessage[0]?.classList.add('highlighted-chat-room-element');
		setTimeout(() => {
			targetMessage[0]?.classList.remove('highlighted-chat-room-element');
		}, 6000);
		targetMessage[0]?.scrollIntoView({ behavior: 'auto' });
	};

	useEffect(() => {
		const chatId = chatInFocus?.id ?? chatInFocus?._id;
		if (!chatId) {
			navigate('/chat');
			return;
		}
		setLoggedInUserParticipantId(
			chatInFocus.participants?.find((participant) =>
				isSameUserId(participant.userId, toUserId(loggedInUserDetails.id))
			)
		);

		let isCurrentChat = true;
		chatIdRef.current = chatId;
		nextMessageOffsetRef.current = 0;
		pendingInitialScrollRef.current = true;
		setChatRoomItems([]);
		setLastSeenMessagedIds([]);
		setLastSeenMessagedIdsSent([]);
		setHasMoreMessages(!pinnedMessageInfocus);

		const loadInitialMessages = async () => {
			try {
				const messages = await loadChatMessages({
					chatId,
					offset: 0,
					limit: 25,
					pinnedMessageId: pinnedMessageInfocus?.chatMessageId?.id,
				}).unwrap();
				if (!isCurrentChat || chatIdRef.current !== chatId) {
					return;
				}

				setChatRoomItems(messages.slice().reverse());
				nextMessageOffsetRef.current = messages.length;
				setHasMoreMessages(!pinnedMessageInfocus && messages.length === 25);
			} catch (error) {
				if (isCurrentChat) {
					console.warn('Unable to load chat messages:', error);
				}
			}
		};

		loadInitialMessages();
		return () => {
			isCurrentChat = false;
		};
	}, [chatInFocus, loadChatMessages, loggedInUserDetails.id, navigate, pinnedMessageInfocus]);

	useLayoutEffect(() => {
		if (!pendingInitialScrollRef.current || chatRoomItems.length === 0) return;
		pendingInitialScrollRef.current = false;

		if (pinnedMessageInfocus) {
			scrollToPInnedMessage();
			return;
		}

		scrollMessagesToLatest();
	}, [chatRoomItems, pinnedMessageInfocus]);

	useEffect(() => {
		const container = messagesListRef.current;
		if (
			!pinnedMessageInfocus &&
			chatRoomItems.length === 25 &&
			container &&
			container.scrollHeight <= container.clientHeight
		) {
			loadOlderMessages();
		}
	}, [chatRoomItems, pinnedMessageInfocus]);

	const loadOlderMessages = async () => {
		const chatId = chatIdRef.current;
		if (!chatId || !hasMoreMessages || isLoadingOlderMessagesRef.current) {
			return;
		}

		const scrollContainer = messagesListRef.current;
		const previousHeight = scrollContainer?.scrollHeight ?? 0;
		isLoadingOlderMessagesRef.current = true;
		setIsLoadingOlderMessages(true);
		try {
			const messages = await loadChatMessages({ chatId, offset: nextMessageOffsetRef.current, limit: 25 }).unwrap();
			if (chatIdRef.current !== chatId) {
				return;
			}

			const olderMessages = messages.slice().reverse();
			setChatRoomItems((currentMessages) => {
				const existingMessageIds = new Set(currentMessages.map((message) => message.id));
				return [...olderMessages.filter((message) => !existingMessageIds.has(message.id)), ...currentMessages];
			});
			nextMessageOffsetRef.current += messages.length;
			setHasMoreMessages(messages.length === 25);
			requestAnimationFrame(() => {
				if (scrollContainer) {
					scrollContainer.scrollTop += scrollContainer.scrollHeight - previousHeight;
					if (messages.length === 25 && scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
						loadOlderMessages();
					}
				}
			});
		} catch (error) {
			console.warn('Unable to load older chat messages:', error);
		} finally {
			isLoadingOlderMessagesRef.current = false;
			setIsLoadingOlderMessages(false);
		}
	};

	const handleMessagesScroll = () => {
		if ((messagesListRef.current?.scrollTop ?? 1) <= 24) {
			loadOlderMessages();
		}
	};

	const updateSeenStatus = (updates: ChatRoomItemsInterface[]) => {
		const updatesByMessageId = new Map(updates.map((message) => [message.id, message]));
		setChatRoomItems((currentMessages) =>
			currentMessages.map((message) => updatesByMessageId.get(message.id) ?? message)
		);
	};

	// hooks for updating the chat room items (the array of the data being rendered onto the screen)
	useEffect(() => {
		setLastSeenMessagedIds(chatRoomItems.map((chat) => chat.id));
	}, [chatRoomItems]);

	useEffect(() => {
		const chatId = chatIdRef.current;
		if (!chatId || lastSeenMessagedIds.length === 0 || _.isEqual(lastSeenMessagedIdsSent, lastSeenMessagedIds)) {
			return;
		}

		markMessagesSeen({ chatId, messageIds: lastSeenMessagedIds })
			.unwrap()
			.then((updatedMessages) => {
				setLastSeenMessagedIdsSent(lastSeenMessagedIds);
				if (updatedMessages.length > 0) {
					updateSeenStatus(updatedMessages);
				}
			})
			.catch((error) => console.warn('Unable to mark messages as seen:', error));
	}, [lastSeenMessagedIds, lastSeenMessagedIdsSent, markMessagesSeen]);

	// initialize event bindings on the screen for the chat room functionailty to be initialised
	useEffect(() => {
		socket.on('NEW_MESSAGE', newMessageListener);
		socket.on('NEW_SEEN_MESSAGES', newSeenMessageListener);
		socket.on('DELETE_MESSAGE', deleteMessageListener);
		socket.on('DELETE_ALL_MESSAGES', deleteAllMessagesListener);

		return () => {
			socket.off('NEW_MESSAGE', newMessageListener);
			socket.off('NEW_SEEN_MESSAGES', newSeenMessageListener);
			socket.off('DELETE_MESSAGE', deleteMessageListener);
			socket.off('DELETE_ALL_MESSAGES', deleteAllMessagesListener);
		};
	}, [socket]);

	const newMessageListener = (payload: any) => {
		setNewMessage(payload);
	};

	useEffect(() => {
		updateChatList(chatListsItems, newMessage, chatInFocus, loggedInUserDetails, dispatch);
	}, [newMessage]);

	const deleteMessageListener = (payload: DeleteMessagePayloadInterface) => {
		if (`${payload.chatId}` === `${chatIdRef.current}`) {
			setChatRoomItems((currentMessages) =>
				currentMessages.filter((message) => `${message.id}` !== `${payload.messageId}`)
			);
		}
	};

	const deleteAllMessagesListener = (payload: any) => {
		if (`${payload?.chatId}` === `${chatIdRef.current}`) {
			setChatRoomItems([]);
		}
	};

	const newSeenMessageListener = (payload: any) => {
		if (payload.length > 0) {
			setNewSeenMessages(payload);
		}
	};

	// hooks for the the messages being viewed by the users on the screen to update the status of the double ticks
	useEffect(() => {
		if (newSeenMessages.length > 0) {
			updateSeenStatus(
				newSeenMessages.map((message) => ({
					...message,
					participantId: message.participant ?? message.participantId,
				}))
			);
		}
	}, [newSeenMessages]);

	const sendChatClicked = () => {
		const chatId = chatIdRef.current;
		if (!chatId || chatRoomItems.length === 0) {
			return;
		}
		markMessagesSeen({ chatId, messageIds: chatRoomItems.map((chat) => chat.id) })
			.unwrap()
			.then(updateSeenStatus)
			.catch((error) => console.warn('Unable to mark messages as seen:', error));
	};

	// dismount event listerners when the message is being dismounted
	useEffect(() => {
		return () => {
			setChatRoomItems([]);
			setNewMessage({});
		};
	}, []);

	// Hooks connected to the sockets for subscribing to the new messages being emittmed from the backend
	useEffect(() => {
		if (!_.isEmpty(newMessage)) {
			if (`${newMessage.chatId}` === `${chatIdRef.current}`) {
				setChatRoomItems((currentMessages) =>
					currentMessages.some((message) => message.id === newMessage.id)
						? currentMessages
						: [...currentMessages, newMessage]
				);
				// do not scroll to bottom if the user has scrolled up and new message is recieved
				if (messagesListRef?.current) {
					const { scrollTop, scrollHeight, clientHeight } = messagesListRef?.current;
					const bottom = scrollHeight - scrollTop - clientHeight <= 100;
					if (bottom) {
						setTimeout(() => {
							// need this setimtime to render the DOM before scrolling to the bottom
							scrollMessagesToLatest('smooth');
						}, 0);
					} else {
						setScrollToBottomNewMessage(true);
					}
				}
			}
		}
	}, [newMessage]);

	// logic for popping up new message notification hanging on the screen for the user to click to scroll to the bottom
	const scrollToBottomTrigger = () => {
		setScrollToBottomNewMessage(false);
		scrollMessagesToLatest('smooth');
	};

	const displayTimeSeparator = (chatRoomItems: ChatRoomItemsInterface[], index: number): boolean => {
		const chatListMessageCreatedDate = chatRoomItems.map((ele) => ele.createdAt);

		const checkTimeDifference =
			moment(chatListMessageCreatedDate[index - 1]).format('DD MM YY') !==
				moment(chatListMessageCreatedDate[index]).format('DD MM YY') || index === 0;

		return checkTimeDifference;
	};

	const renderTimeSeparator = (chatItem: ChatRoomItemsInterface) => {
		return (
			<div className="my-5 text-center">
				{moment(chatItem.createdAt).format('DD MM YY') === moment().format('DD MM YY') ? (
					<span className="text-sm text-gray-500">{moment(chatItem.createdAt).format('[Today]')}</span>
				) : (
					<span className="text-sm text-gray-500">{moment(chatItem.createdAt).format('DD MMMM YYYY')}</span>
				)}
			</div>
		);
	};

	return (
		<div className="chat-room-root flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-gray-50">
			<div className="relative min-h-0 flex-1">
				<div
					className="h-full w-full overflow-x-hidden overflow-y-auto px-3 py-2 sm:px-4"
					ref={messagesListRef}
					onScroll={handleMessagesScroll}
				>
					{isLoadingOlderMessages && <div className="py-2 text-center text-sm text-muted-foreground">Loading messages...</div>}
						{chatRoomItems.map((chatItem, index) => {
							{
								/* loop to get the chat messages and the details */
							}
							return (
								<div
									key={chatItem.id}
									// style={{ backgroundColor: '#FAFAFA', display: 'block', height: '70px' }}
									className={`${chatItem.id}`}
								>
									<div>
										{displayTimeSeparator(chatRoomItems, index)
											? renderTimeSeparator(chatItem)
											: ''}
									</div>
									{/* message */}
									<div key={chatItem.id}>
										<ChatRoomMessage
											loggedInUserParticipantId={loggedInUserParticipantId}
											chatItem={chatItem}
											setChatRoomItems={setChatRoomItems}
											chatRoomItems={chatRoomItems}
											chatInFocus={chatInFocus}
										/>
									</div>
									{/* message time and read and unread receipts */}
									<div>
										<ChatRoomMessageDetails
											loggedInUserParticipantId={loggedInUserParticipantId}
											chatItem={chatItem}
											chatInFocus={chatInFocus}
										/>
									</div>
								</div>
							);
						})}
				</div>
				{/* Logic for showing a new message has been recieved or not and whether to scroll down to view it */}
				<ScrollToBottom
					scrollToBottomNewMessage={scrollToBottomNewMessage}
					scrollToBottomTrigger={scrollToBottomTrigger}
				/>
				{files.length > 0 && (
					<div className="absolute inset-0 z-10 overflow-y-auto bg-gray-50">
						<ChatRoomAttachmentFile files={files} setFiles={setFiles} />
					</div>
				)}
			</div>
				<div className="chat-composer flex w-full min-w-0 shrink-0 items-center border-t border-solid bg-white px-3 py-2">
					<SendChatMessage
						updateSeenMessages={sendChatClicked}
						files={files}
						setFiles={setFiles}
						loggedInUserDetails={loggedInUserDetails}
					/>
				</div>
		</div>
	);
}
